import { IniData, parseIni } from './ini';
import { JuniValues, LowerCaseSet } from './JuniValues';
import { KnyttPoint } from './KnyttPoint';

const KEY_NAMES = [
  'Creatures 1',
  'Creatures 2',
  'Coins 1',
  'Coins 2',
  'Coins 3',
  'Coins 4',
  'Artifacts 1',
  'Artifacts 2',
];

// [Extras] keys the port owns (lowercased) — everything else round-trips raw.
// 'Gzipped Visited Areas' is intentionally dropped, not passed through: the port
// always writes plain 'Visited Areas', and yknytt's reader would prefer a stale
// gzipped copy over it.
const KNOWN_EXTRAS = new Set([
  ...KEY_NAMES.map((k) => k.toLowerCase()),
  'coins spent',
  'creature count',
  'coin count',
  ...[1, 2, 3, 4, 5, 6, 7].map((i) => `artifact count ${i}`),
  'visited areas',
  'gzipped visited areas',
  'marked',
  'attach',
  'character',
  'tint',
  'tintink',
  'tinttrans',
  'cutscenes',
  'endings',
  'total deaths',
  'hardest place',
  'hardest place deaths',
  'total time',
]);

/**
 * A save slot, format-compatible with YKnytt/Knytt Stories save INIs.
 * Stores position, powers, flags and packed collectables.
 */
export class KnyttSave {
  private positions = { xMap: 0, yMap: 0, xPos: 0, yPos: 0 };
  private powers: boolean[] = new Array(13).fill(false);
  private flags: boolean[] = new Array(10).fill(false);
  private collectables: boolean[] = new Array(200).fill(false);
  private coinsSpent = 0;
  private extra = new Map<string, string>(); // raw passthrough for unknown Extras (original-case keys)
  worldFolder: string | null = null; // [World] World Folder (KnyttSave.cs setWorldDirectory)
  private visited = new Set<number>();
  private marked = new Map<string, string>(); // KnyttPoint.key -> collected char(s)
  private attachment: string | null = null;
  private character: string | null = null;
  private tint: [string | null, string | null, string | null] = [null, null, null];
  private cutscenes = new Set<string>();
  private endings = new Set<string>();
  private totalDeaths = 0;
  private hardestPlace: string | null = null;
  private hardestPlaceDeaths = 0;
  private totalTime = 0;

  static parse(text: string): KnyttSave {
    const save = new KnyttSave();
    const ini = parseIni(text);
    save.readFromIni(ini);
    return save;
  }

  private readFromIni(ini: IniData): void {
    this.worldFolder = ini.getSection('World')?.get('World Folder') ?? null;
    const pos = ini.getSection('Positions');
    if (pos) {
      this.positions.xMap = intOf(pos.get('X Map'), 0);
      this.positions.yMap = intOf(pos.get('Y Map'), 0);
      this.positions.xPos = intOf(pos.get('X Pos'), 0);
      this.positions.yPos = intOf(pos.get('Y Pos'), 0);
    }
    const pow = ini.getSection('Powers');
    if (pow) {
      for (let i = 0; i < 13; i++) this.powers[i] = intOf(pow.get(`Power${i}`), 0) !== 0;
    }
    const fl = ini.getSection('Flags');
    if (fl) {
      for (let i = 0; i < 10; i++) this.flags[i] = intOf(fl.get(`Flag${i}`), 0) !== 0;
    }
    const ex = ini.getSection('Extras');
    if (ex) {
      const capacity = [25, 25, 25, 25, 25, 25, 28, 21];
      let posIdx = 0;
      for (let i = 0; i < KEY_NAMES.length; i++) {
        const packed = intOf(ex.get(KEY_NAMES[i]), 0);
        for (let j = 0; j < capacity[i]; j++) {
          this.collectables[++posIdx] = (packed & (1 << j)) !== 0;
        }
      }
      this.coinsSpent = intOf(ex.get('Coins Spent'), 0);

      const visitedStr = ex.get('Visited Areas');
      if (visitedStr) {
        const words = visitedStr.split(',').map((s) => Number.parseInt(s.trim(), 10) | 0);
        for (let w = 0; w < words.length; w++) {
          for (let bit = 0; bit < 32; bit++) {
            if (words[w] & (1 << bit)) this.visited.add(w * 32 + bit);
          }
        }
      }
      const markedStr = ex.get('Marked');
      if (markedStr) {
        for (const part of markedStr.split(',')) {
          const xi = part.indexOf('x');
          const yi = part.indexOf('y', xi + 1);
          if (xi < 0 || yi < 0) continue;
          const value = part.slice(0, xi);
          const x = Number.parseInt(part.slice(xi + 1, yi), 10);
          const y = Number.parseInt(part.slice(yi + 1), 10);
          this.marked.set(new KnyttPoint(x, y).key, value);
        }
      }
      this.attachment = ex.get('Attach');
      this.character = ex.get('Character');
      this.tint = [ex.get('Tint') ?? null, ex.get('TintInk') ?? null, ex.get('TintTrans') ?? null];
      const cutscenesStr = ex.get('Cutscenes');
      if (cutscenesStr) this.cutscenes = new LowerCaseSet(cutscenesStr.split(',').filter(Boolean));
      const endingsStr = ex.get('Endings');
      if (endingsStr) this.endings = new LowerCaseSet(endingsStr.split(',').filter(Boolean));
      this.totalDeaths = intOf(ex.get('Total Deaths'), 0);
      this.hardestPlace = ex.get('Hardest Place');
      this.hardestPlaceDeaths = intOf(ex.get('Hardest Place Deaths'), 0);
      this.totalTime = intOf(ex.get('Total Time'), 0);
      for (const key of ex.keys()) {
        if (!KNOWN_EXTRAS.has(key)) this.extra.set(ex.originalKey(key), ex.get(key) ?? '');
      }
    }
  }

  getArea(): KnyttPoint {
    return new KnyttPoint(this.positions.xMap, this.positions.yMap);
  }
  setArea(p: KnyttPoint): void {
    this.positions.xMap = p.x;
    this.positions.yMap = p.y;
  }
  getAreaPosition(): KnyttPoint {
    return new KnyttPoint(this.positions.xPos, this.positions.yPos);
  }
  setAreaPosition(p: KnyttPoint): void {
    this.positions.xPos = p.x;
    this.positions.yPos = p.y;
  }

  /** Populate JuniValues from this save. */
  readInto(values: JuniValues): void {
    values.powers = [...this.powers];
    values.flags = [...this.flags];
    values.collectables = [...this.collectables];
    values.coinsSpent = this.coinsSpent;
    values.visited = new Set(this.visited);
    values.marked = new Map(this.marked);
    values.attachment = this.attachment;
    values.character = this.character;
    values.tint = [...this.tint];
    values.cutscenes = new LowerCaseSet(this.cutscenes);
    values.endings = new LowerCaseSet(this.endings);
    values.totalDeaths = this.totalDeaths;
    values.hardestPlace = this.hardestPlace;
    values.hardestPlaceDeaths = this.hardestPlaceDeaths;
    values.totalTime = this.totalTime;
  }

  /** Copy JuniValues + position into this save. */
  writeFrom(values: JuniValues): void {
    this.powers = [...values.powers];
    this.flags = [...values.flags];
    this.collectables = [...values.collectables];
    this.coinsSpent = values.coinsSpent;
    this.visited = new Set(values.visited);
    this.marked = new Map(values.marked);
    this.attachment = values.attachment;
    this.character = values.character;
    this.tint = [...values.tint];
    this.cutscenes = new LowerCaseSet(values.cutscenes);
    this.endings = new LowerCaseSet(values.endings);
    this.totalDeaths = values.totalDeaths;
    this.hardestPlace = values.hardestPlace;
    this.hardestPlaceDeaths = values.hardestPlaceDeaths;
    this.totalTime = values.totalTime;
  }

  /** Win.cs: record an ending into the save without touching position/powers. */
  addEnding(ending: string): void {
    this.endings.add(ending);
  }

  /** Win.cs: copy the live run stats into the save (deaths/hardest place/time only). */
  writeStats(values: JuniValues): void {
    this.totalDeaths = values.totalDeaths;
    this.hardestPlace = values.hardestPlace;
    this.hardestPlaceDeaths = values.hardestPlaceDeaths;
    this.totalTime = values.totalTime;
  }

  /**
   * Replace the stored map marks. The incoming-area checkCollectables writes its
   * marks into the save snapshot (C# CurrentSave.SourcePowers.Marked), so they
   * surface after a respawn restores values from the save.
   */
  setMarked(marked: Map<string, string>): void {
    this.marked = new Map(marked);
  }

  toString(): string {
    const lines: string[] = [];
    if (this.worldFolder != null) {
      lines.push('[World]');
      lines.push(`World Folder=${this.worldFolder}`);
    }
    lines.push('[Positions]');
    lines.push(`X Map=${this.positions.xMap}`);
    lines.push(`Y Map=${this.positions.yMap}`);
    lines.push(`X Pos=${this.positions.xPos}`);
    lines.push(`Y Pos=${this.positions.yPos}`);
    lines.push('[Powers]');
    for (let i = 0; i < 13; i++) lines.push(`Power${i}=${this.powers[i] ? 1 : 0}`);
    lines.push('[Flags]');
    for (let i = 0; i < 10; i++) lines.push(`Flag${i}=${this.flags[i] ? 1 : 0}`);
    lines.push('[Extras]');
    // Collectable keys are omitted entirely when nothing is collected (KnyttSave.cs:58).
    if (this.collectables.includes(true) || this.coinsSpent !== 0) {
      const packed = new Array(8).fill(0);
      for (let i = 0; i < this.collectables.length - 1; i++) {
        if (this.collectables[i + 1]) packed[Math.floor(i / 25)] |= 1 << i % 25;
      }
      packed[6] |= (packed[7] & 0b111) << 25;
      packed[7] >>>= 3;
      for (let i = 0; i < KEY_NAMES.length; i++) lines.push(`${KEY_NAMES[i]}=${packed[i] >>> 0}`);
      let creatures = 0;
      for (let i = 1; i <= 50; i++) if (this.collectables[i]) creatures++;
      let coins = 0;
      for (let i = 51; i <= 150; i++) if (this.collectables[i]) coins++;
      lines.push(`Creature Count=${creatures}`);
      lines.push(`Coin Count=${coins - this.coinsSpent}`);
      lines.push(`Coins Spent=${this.coinsSpent}`);
      for (let i = 0; i < 7; i++) {
        let artifacts = 0;
        for (let j = 151 + i * 7; j < 151 + (i + 1) * 7; j++) if (this.collectables[j]) artifacts++;
        lines.push(`Artifact Count ${i + 1}=${artifacts}`);
      }
    }
    if (this.visited.size > 0) {
      const maxIdx = Math.max(...this.visited);
      const words = new Array(Math.floor(maxIdx / 32) + 1).fill(0);
      for (const idx of this.visited) words[Math.floor(idx / 32)] |= 1 << idx % 32;
      lines.push(`Visited Areas=${words.map((w) => w | 0).join(',')}`);
    }
    if (this.marked.size > 0) {
      const parts: string[] = [];
      for (const [key, value] of this.marked) {
        const [x, y] = key.split(',').map(Number);
        parts.push(`${value}x${x}y${y}`);
      }
      lines.push(`Marked=${parts.join(',')}`);
    }
    if (this.attachment != null) lines.push(`Attach=${this.attachment}`);
    lines.push(`Character=${(this.character ?? 'juni').toLowerCase()}`);
    if (this.tint[0] != null) lines.push(`Tint=${this.tint[0]}`);
    if (this.tint[1] != null) lines.push(`TintInk=${this.tint[1]}`);
    if (this.tint[2] != null) lines.push(`TintTrans=${this.tint[2]}`);
    if (this.cutscenes.size > 0) lines.push(`Cutscenes=${[...this.cutscenes].join(',')}`);
    if (this.endings.size > 0) lines.push(`Endings=${[...this.endings].join(',')}`);
    lines.push(`Total Deaths=${this.totalDeaths}`);
    if (this.hardestPlace != null) lines.push(`Hardest Place=${this.hardestPlace}`);
    lines.push(`Hardest Place Deaths=${this.hardestPlaceDeaths}`);
    lines.push(`Total Time=${Math.round(this.totalTime)}`);
    for (const [key, value] of this.extra) lines.push(`${key}=${value}`);
    return lines.join('\r\n') + '\r\n';
  }
}

function intOf(s: string | null | undefined, def: number): number {
  if (s == null) return def;
  const n = Number.parseInt(s.trim(), 10);
  return Number.isNaN(n) ? def : n;
}
